<div class="hotels-shell">
	<!-- Legend (same pattern as High Risk Reports) -->
	<div class="toolbar">
		<div class="legend">
			<details class="legend-details">
				<summary class="legend-summary">
					<div class="legend-summary-left">
						<i class="pi pi-info-circle"></i>
						<span class="legend-summary-title">Risk thresholds (average per day)</span>

						<span class="legend-summary-inline">
							High ≥ {{ thresholds.highMinAmount | currency:'USD':'symbol':'1.0-0' }} •
							Medium
							{{ thresholds.mediumMinAmount | currency:'USD':'symbol':'1.0-0' }}–{{ thresholds.mediumMaxAmount | currency:'USD':'symbol':'1.0-0' }}
							•
							Low ≤ {{ thresholds.lowMaxAmount | currency:'USD':'symbol':'1.0-0' }}
						</span>
					</div>

					<i class="pi pi-chevron-down legend-chevron"></i>
				</summary>

				<div class="legend-body">
					<span class="legend-item">
						<span class="pill risk-high">High</span>
						<span class="legend-text">
							By Hotel High Risk: ≥ {{ thresholds.highMinAmount | currency:'USD':'symbol':'1.0-0' }}
						</span>
					</span>

					<span class="legend-item">
						<span class="pill risk-medium">Medium</span>
						<span class="legend-text">
							By Hotel Medium Risk: Between
							{{ thresholds.mediumMinAmount | currency:'USD':'symbol':'1.0-0' }}
							and
							{{ thresholds.mediumMaxAmount | currency:'USD':'symbol':'1.0-0' }}
						</span>
					</span>

					<span class="legend-item">
						<span class="pill risk-low">Low</span>
						<span class="legend-text">
							By Hotel Low Risk: ≤ {{ thresholds.lowMaxAmount | currency:'USD':'symbol':'1.0-0' }}
						</span>
					</span>
				</div>
			</details>
		</div>
	</div>

	<!-- Loading skeleton -->
	<div *ngIf="loading === true" class="list">
		<div class="row skeleton" *ngFor="let i of [1,2,3,4,5]">
			<div class="left">
				<p-skeleton width="55%" height="1.2rem"></p-skeleton>
				<p-skeleton width="35%" height="0.9rem" styleClass="mt-2"></p-skeleton>
			</div>

			<div class="right">
				<p-skeleton width="7.5rem" height="1.2rem"></p-skeleton>
				<p-skeleton width="3.5rem" height="0.9rem" styleClass="mt-2"></p-skeleton>
			</div>

			<div class="arrow">
				<p-skeleton width="1.2rem" height="1.2rem"></p-skeleton>
			</div>
		</div>
	</div>

	<!-- Table (only render when NOT loading) -->
	<ng-container *ngIf="loading === false">
		<p-table
			[value]="rows"
			dataKey="propertyId"
			rowExpandMode="multiple"
			[expandedRowKeys]="expandedRowKeys"
		(onRowExpand)="onRowExpand($event)"
		(onRowCollapse)="onRowCollapse($event)"
		[sortMode]="'single'"
		[rows]="5"
		[paginator]="rows.length > 5"
		responsiveLayout="scroll"
		>
			<ng-template pTemplate="header">
				<tr>
					<th pSortableColumn="hotelName">
						Hotel Name
						<p-sortIcon field="hotelName"></p-sortIcon>
					</th>

					<th pSortableColumn="reportsAtRisk">
						No. Reports at Risk
						<p-sortIcon field="reportsAtRisk"></p-sortIcon>
					</th>

					<th pSortableColumn="financialImpact">
						Financial Impact
						<p-sortIcon field="financialImpact"></p-sortIcon>
					</th>

					<th pSortableColumn="statusRank">
						Status
						<p-sortIcon field="statusRank"></p-sortIcon>
					</th>

					<th style="width: 48px;"></th>
				</tr>
			</ng-template>

			<!-- CHANGED: use row toggler + expanded state -->
			<ng-template pTemplate="body" let-row let-expanded="expanded">
				<tr>
					<td>{{ row.hotelName }}</td>
					<td>{{ row.reportsAtRisk }}</td>
					<td>
						{{ row.financialImpact | currency:'USD':'symbol':'1.0-0' }}
						<span class="avg" *ngIf="daysInRange > 0">
							({{ avgDailyImpact(row) | currency:'USD':'symbol':'1.0-0' }})
						</span>
					</td>
					<td>
						<span class="pill" [ngClass]="riskClass(row.status)">{{ row.status }}</span>
					</td>

					<td class="text-right">
						<button
							pButton
							type="button"
							class="p-button-text"
							[pRowToggler]="row"
							[icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
							aria-label="Toggle hotel reports"
						></button>
					</td>
				</tr>
			</ng-template>

			<!-- drawer row under hotel -->
			<ng-template pTemplate="rowexpansion" let-row>
				<tr class="drawer-row">
					<td colspan="5">
						<div class="drawer">
							<!-- Financial  -->
							<div class="drawer-section">
								<div class="drawer-title">Financial Reports</div>

								<div *ngIf="(row.financialReports?.length ?? 0) > 0; else noFinancial">
									<table class="drawer-table">
										<thead>
										<tr>
											<th class="date-cell">Date</th>
											<th>Report Name</th>
											<th class="drawer-amount">Amount</th>
										</tr>
										</thead>

										<tbody>
										<tr
											*ngFor="let r of row.financialReports; trackBy: trackByReport"
											[class.is-zero]="(r.amount ?? 0) === 0"
										>
											<td class="date-cell">{{ r.date }}</td>
											<td class="report-name">
												<ng-container *ngIf="r.taskInstanceId; else financialPlainName">
													<a
														href=""
														class="task-link"
														title="Open task"
														(click)="goToTaskFromReport(row, r, $event)"
													>
														{{ r.reportName }}
													</a>
												</ng-container>

												<ng-template #financialPlainName>
													<span title="{{ r.reportName }}">{{ r.reportName }}</span>
												</ng-template>
											</td>
											<td class="drawer-amount">
												{{ r.amount | currency:'USD':'symbol':'1.0-0' }}
											</td>
										</tr>
										</tbody>
									</table>
								</div>

								<ng-template #noFinancial>
									<div class="drawer-empty">No financial reports for this period.</div>
								</ng-template>
							</div>

							<!-- Operational -->
							<div class="drawer-section">
								<div class="drawer-title">Operational Reports</div>

								<div *ngIf="(row.operationalReports?.length ?? 0) > 0; else noOperational">
									<table class="drawer-table">
										<thead>
										<tr>
											<th class="date-cell">Date</th>
											<th>Report Name</th>
											<th class="drawer-amount">Items</th> <!-- CHANGED -->
										</tr>
										</thead>

										<tbody>
										<tr
											*ngFor="let r of row.operationalReports; trackBy: trackByReport"
											[class.is-zero]="(r.amount ?? 0) === 0"
										>
											<td class="date-cell">{{ r.date }}</td>
											<td class="report-name" title="{{ r.reportName }}">{{ r.reportName }}</td>
											<td class="drawer-amount">{{ r.amount | number:'1.0-0' }}</td>
										</tr>
										</tbody>
									</table>
								</div>

								<ng-template #noOperational>
									<div class="drawer-empty">No operational reports for this period.</div>
								</ng-template>
							</div>
						</div>
					</td>
				</tr>
			</ng-template>

			<ng-template pTemplate="emptymessage">
				<tr>
					<td colspan="5" class="empty">No data available for the selected range.</td>
				</tr>
			</ng-template>
		</p-table>
	</ng-container>
</div>
